import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fetchGalleryImages } from '../lib/sanityClient';
import Lightbox from '../components/Lightbox';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const data = await fetchGalleryImages();
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevImage = () => {
    const newIndex = selectedImage ? images.findIndex(img => img._id === selectedImage.data._id) - 1 : 0;
    const index = newIndex < 0 ? images.length - 1 : newIndex;
    setSelectedImage({
      url: images[index].image?.asset?.url,
      data: images[index]
    });
  };

  const handleNextImage = () => {
    const currentIndex = selectedImage ? images.findIndex(img => img._id === selectedImage.data._id) : 0;
    const newIndex = (currentIndex + 1) % images.length;
    setSelectedImage({
      url: images[newIndex].image?.asset?.url,
      data: images[newIndex]
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  if (loading) {
    return (
      <div className="bg-Pink-base w-full">
        <Header />
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-Pink-main"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-Pink-base w-full">
      <Header onNavigateGallery={() => {}} />

      <motion.div
        className="w-full py-12 md:py-20 px-4 md:px-10 lg:px-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        {/* Page Title */}
        <motion.div className="mb-12" variants={itemVariants}>
          <h1 className="text-4xl md:text-5xl font-semibold text-Neautral-main font-opensans mb-4">
            Galleri
          </h1>
          <p className="text-Neautral-light font-opensans text-lg">
            Se alle våre arbeider og inspirasjon
          </p>
        </motion.div>

        {images.length === 0 ? (
          <motion.div
            className="text-center py-20 text-Neautral-light font-opensans"
            variants={itemVariants}
          >
            Ingen bilder i galleriet ennå
          </motion.div>
        ) : (
          <>
            {/* Thumbnail Grid */}
            <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {images.map((image, index) => (
                <motion.div
                  key={image._id}
                  className="relative group cursor-pointer rounded-[15px] overflow-hidden aspect-square"
                  onClick={() => {
                    setSelectedImage({
                      url: image.image?.asset?.url,
                      data: image
                    });
                    setLightboxOpen(true);
                  }}
                  variants={itemVariants}
                  whileHover={{ scale: 0.95 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {image.image?.asset?.url && (
                    <>
                      <img
                        src={image.image.asset.url}
                        alt={image.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition duration-300 flex items-center justify-center">
                        <p className="text-white font-semibold font-opensans text-center px-4 opacity-0 group-hover:opacity-100 transition duration-300">
                          {image.title}
                        </p>
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </>
        )}
      </motion.div>

      {/* Lightbox Modal */}
      <Lightbox
        isOpen={lightboxOpen}
        imageSrc={selectedImage?.url}
        imageData={selectedImage?.data}
        onClose={() => setLightboxOpen(false)}
        onPrev={handlePrevImage}
        onNext={handleNextImage}
      />

      <Footer onShowAdmin={() => {}} />
      <BackToTop />
    </div>
  );
}
